import type { Layout } from '../../data/types';
import type { SiteModel } from '../../site/types';
import type { Ring } from '../../geo/types';
import type { LifeRoute, LifeStep } from './types';
import { centroid, distancePointSegment, pointInRing } from '../../geo/polygon';
import { buildStreetGraph, distance } from './streetGraph';

/** Religious residents carry out courtyard chores inside their community, not in arbitrary homes. */
export function precinctRoutines(
  layout: Layout,
  site: SiteModel,
  obstacles: readonly Ring[]
): LifeRoute[] {
  const routes: LifeRoute[] = [];
  for (const [role, id] of [
    ['sister', 'landmark-sint-lucien'],
    ['friar', 'landmark-minderbroeders'],
  ] as const) {
    const landmark = layout.landmarks.find((l) => l.id === id);
    if (!landmark) continue;
    const graph = buildStreetGraph(
      {
        ...layout,
        streets: [],
        squares: [
          { id, name: landmark.name, polygon: landmark.footprint, sourceRefs: landmark.sourceRefs },
        ],
      },
      {
        clear(p, radius) {
          const near = radius + 0.15;
          if (site.waterIndex.contains(p) || !pointInRing(p, landmark.footprint)) return false;
          const rings = [landmark.footprint, ...obstacles];
          return rings.every(
            (ring, i) =>
              (i === 0 || !pointInRing(p, ring)) &&
              ring.every(
                (a, j) => distancePointSegment(p, { a, b: ring[(j + 1) % ring.length]! }) >= near
              )
          );
        },
      }
    );
    const centre = centroid(landmark.footprint);
    const sorted = graph.nodes
      .map((node, i) => ({ node, i }))
      .sort((a, b) => distance(a.node.position, centre) - distance(b.node.position, centre));
    for (let n = 0; n < 4; n++) {
      const origin = sorted[n * 2];
      if (!origin) break;
      const destinations = sorted.filter(
        ({ node }) => distance(node.position, origin.node.position) > 12
      );
      for (const destination of destinations) {
        const path = graph.path(origin.i, destination.i);
        if (!path) continue;
        const third = destinations.find(
          (candidate) =>
            candidate.i !== destination.i &&
            distance(candidate.node.position, destination.node.position) > 8 &&
            graph.path(destination.i, candidate.i)
        );
        if (!third) continue;
        const paths = [path, graph.path(destination.i, third.i)!, graph.path(third.i, origin.i)!];
        const speed = 0.78 + n * 0.04;
        const steps = choreSteps(paths, speed, n);
        const duration = steps.reduce((sum, step) => sum + step.seconds, 0);
        routes.push({
          id: `${role}-precinct-${n}`,
          role,
          streetId: id,
          a: path[0]!,
          b: path.at(-1)!,
          radius: 0.48,
          speed,
          scale: 0.95 + n * 0.015,
          phase: (duration * n) / 4,
          daytime: true,
          routine: steps,
        });
        break;
      }
    }
  }
  return routes;
}

function choreSteps(
  paths: readonly (readonly import('../../geo/types').Vec2[])[],
  speed: number,
  resident: number
): LifeStep[] {
  const steps: LifeStep[] = [];
  for (const [j, points] of paths.entries()) {
    for (let k = 1; k < points.length; k++) {
      const from = points[k - 1]!,
        to = points[k]!;
      steps.push({
        from,
        to,
        seconds: distance(from, to) / speed,
        action: 'walk',
        heading: Math.atan2(to[1] - from[1], to[0] - from[0]),
        prop: j === 1 ? 'basket' : 'parcel',
      });
    }
    const at = points.at(-1)!;
    steps.push({
      from: at,
      to: at,
      seconds: 14 + resident * 4,
      action: j === 1 ? 'work' : 'wait',
      heading: steps.at(-1)!.heading,
      prop: j === 1 ? 'basket' : 'parcel',
    });
  }
  return steps;
}
